import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PetsIcon from "@mui/icons-material/Pets";
import HotelRoundedIcon from "@mui/icons-material/HotelRounded";
import CelebrationIcon from "@mui/icons-material/Celebration";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { ServiceDialog } from "./ServiceDialog.tsx";
import dagopvang from "../assets/images/dagopvang.jpg";
import weekendopvang from "../assets/images/weekendopvang.jpg";
import vakantieopvang from "../assets/images/vakantieopvang.jpg";

const StyledServicesSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(9, 0),
  backgroundColor: theme.palette.background.default,
}));

const StyledServicesTitle = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(1.5),
}));

const StyledServicesSubtitle = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  color: theme.palette.text.secondary,
  maxWidth: 700,
  margin: `0 auto ${theme.spacing(5)}`,
}));

const StyledServiceCard = styled(Paper)<{
  $isDarkMode: boolean;
  $tone: "green" | "pink" | "cream";
}>(({ theme, $isDarkMode, $tone }) => {
  const lightBackground =
    $tone === "pink"
      ? "#FFF2F6"
      : $tone === "green"
        ? "#F1F7E8"
        : "#FFF9EE";

  return {
    height: "100%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    borderRadius: theme.spacing(2),
    border: `1px solid ${
      $isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"
    }`,
    backgroundColor: $isDarkMode ? "#3D3D3D" : lightBackground,
    color: $isDarkMode ? "#F0F0F0" : theme.palette.text.primary,
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    boxShadow: theme.shadows[1],
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: theme.shadows[5],
    },
    "&:focus-visible": {
      outline: `3px solid ${theme.palette.primary.main}`,
      outlineOffset: 3,
    },
  };
});

const StyledImageWrap = styled(Box)({
  position: "relative",
  height: 185,
  overflow: "hidden",
});

const StyledServiceImage = styled("img")({
  width: "100%",
  height: "100%",
  display: "block",
  objectFit: "cover",
});

const StyledIconBadge = styled(Box)(({ theme }) => ({
  width: 44,
  height: 44,
  flex: "0 0 44px",
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: theme.shadows[1],
}));

const StyledTitleRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
}));

const StyledCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 3, 3),
  minHeight: 210,
  display: "flex",
  flexDirection: "column",
  flex: 1,
}));

const StyledMore = styled(Box)(({ theme }) => ({
  marginTop: "auto",
  paddingTop: theme.spacing(2),
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: "0.95rem",
}));

export interface Service {
  title: string;
  description: string;
  icon: React.ReactNode;
  detailedDescription: string;
  image: string;
  facts: string[];
}

export const ServicesSection: React.FC = () => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const services: Array<Service & { tone: "green" | "pink" | "cream" }> = [
    {
      title: "Dagopvang",
      description:
        "Een fijne dag bij Pippaya, met wandelen, rust, aandacht en gezelligheid.",
      icon: <PetsIcon fontSize="medium" aria-hidden="true" />,
      detailedDescription:
        "Dagopvang is er voor honden die een fijne dag in een kleine, huiselijke groep komen doorbrengen. We wandelen, spelen en snuffelen, maar bouwen net zo goed rustmomenten in. Zo draait je hond gewoon mee in het ritme van de dag.",
      image: dagopvang,
      facts: [
        "Brengen vanaf 08:00 uur",
        "Ophalen vóór 18:00 uur",
        "Kleinschalige opvang met aandacht en rust",
      ],
      tone: "pink",
    },
    {
      title: "Logeren",
      description:
        "Voor een weekend, een paar nachten of een langere vakantie. Je hond draait hier gewoon mee in huis.",
      icon: <HotelRoundedIcon fontSize="medium" aria-hidden="true" />,
      detailedDescription:
        "Tijdens een logeerperiode hoort een hond bij de club. Polderwandelingen, zwemmen als het kan, spelen, modderpoten, lekkere snackjes, knuffels én voldoende rust. Omdat Pippaya bewust kleinschalig is, leren we onze logeerhonden echt kennen en houden we rekening met hun eigen gewoontes en karakter.",
      image: weekendopvang,
      facts: [
        "Brengen na 14:00 uur",
        "Ophalen vóór 10:00 uur",
        "Kort én langer verblijf",
      ],
      tone: "green",
    },
    {
      title: "Feestdagopvang",
      description:
        "Ook op feestdagen kan je hond gezellig bij Pippaya verblijven.",
      icon: <CelebrationIcon fontSize="medium" aria-hidden="true" />,
      detailedDescription:
        "Ook op feestdagen draait het hier gewoon om een fijne dag voor de hond: wandelen, gezelligheid, aandacht, rust en natuurlijk iets lekkers. Feestdagopvang kan eventueel inclusief één overnachting, zodat de baasjes zonder haast van hun dag of avond kunnen genieten.",
      image: vakantieopvang,
      facts: [
        "Brengen vanaf 10:00 uur",
        "Eventueel inclusief één overnachting",
        "Ophalen vóór 10:00 uur de volgende ochtend",
      ],
      tone: "cream",
    },
  ];

  const openService = (service: Service) => {
    setSelectedService(service);
    setDialogOpen(true);
  };

  return (
    <StyledServicesSection
      id="services"
      component="section"
      aria-labelledby="services-heading"
    >
      <Container maxWidth="lg">
        <StyledServicesTitle variant="h3" component="h2" id="services-heading">
          Opvang bij Pippaya
        </StyledServicesTitle>

        <StyledServicesSubtitle>
          Van een fijne dag tot een langere vakantie: altijd kleinschalig,
          huiselijk en met aandacht voor wat jouw hond nodig heeft.
        </StyledServicesSubtitle>

        <Grid container spacing={3} role="list">
          {services.map((service) => (
            <Grid item xs={12} md={4} key={service.title} role="listitem">
              <StyledServiceCard
                $isDarkMode={isDarkMode}
                $tone={service.tone}
                onClick={() => openService(service)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openService(service);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Meer informatie over ${service.title}`}
              >
                <StyledImageWrap>
                  <StyledServiceImage
                    src={service.image}
                    alt=""
                    aria-hidden="true"
                  />
                </StyledImageWrap>

                <StyledCardContent>
                  <StyledTitleRow>
                    <StyledIconBadge>{service.icon}</StyledIconBadge>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{ fontWeight: 500 }}
                    >
                      {service.title}
                    </Typography>
                  </StyledTitleRow>

                  <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {service.description}
                  </Typography>

                  <StyledMore>
                    Bekijk meer
                    <ArrowForwardRoundedIcon fontSize="small" />
                  </StyledMore>
                </StyledCardContent>
              </StyledServiceCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      <ServiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        service={selectedService}
      />
    </StyledServicesSection>
  );
};
